import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  CreditCard,
  ExternalLink,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ContactLeadPicker,
  type ContactLeadOption,
} from "@/components/ContactLeadPicker";
import { paymentFulfillmentSelectOptions } from "@/lib/paymentFulfillmentDisplay";
import { paymentStatusSelectOptions } from "@/lib/paymentStatusDisplay";
import { normalizeAttendanceStatuses } from "@/lib/ticketAttendance";
import { ticketAttendanceSelectOptions } from "@/lib/ticketAttendanceDisplay";
import { ProductTypeEnum } from "@/types/ProductType";
import type { PaymentFulfillmentStatus } from "@/types/PaymentFulfillmentStatus";
import type { PaymentStatus } from "@/types/PaymentStatus";
import { ProductCatalogPicker } from "@/components/ProductCatalogPicker";
import { partitionProductCatalogOptions } from "@/lib/productCatalogOptions";
import { cn } from "@/lib/utils";

const OBJECT_ID_HEX_RE = /^[a-f0-9]{24}$/i;
const SELECT_NONE = "__none__";

interface CatalogVariant {
  _id: string;
  name?: string;
  price?: number;
}

interface CatalogProduct {
  _id: string;
  name?: string;
  type?: string;
  price?: number;
  archivedAt?: string | null;
  variants?: CatalogVariant[];
}

export interface PurchasedProductLine {
  productId?: string;
  variantId?: string;
  description?: string;
  quantity?: number;
  price?: number;
  attendanceStatuses?: TicketAttendanceStatus[];
}

type ProductLineDraft = {
  clientKey: string;
  productId: string;
  variantId: string;
  description: string;
  quantity: string;
  priceDollars: string;
  attendanceStatuses?: TicketAttendanceStatus[];
};

function ticketQuantityFromDraft({ draft }: { draft: ProductLineDraft }): number {
  const qtyParsed = Number.parseFloat(draft.quantity.trim());
  const quantity = Math.round(qtyParsed);
  if (!Number.isFinite(qtyParsed) || !Number.isInteger(quantity) || quantity < 1) {
    return 1;
  }
  return quantity;
}

function isTicketCatalogProduct({ product }: { product: CatalogProduct | undefined }): boolean {
  return product?.type === ProductTypeEnum.TICKET;
}

function formatCentsAsDollarInput({ cents }: { cents: number }): string {
  return (cents / 100).toFixed(2);
}

function catalogUnitPriceCents({
  product,
  variantId,
}: {
  product: CatalogProduct | undefined;
  variantId?: string;
}): number | undefined {
  if (!product) return undefined;
  const vid = variantId?.trim();
  if (vid && product.variants?.length) {
    const variant = product.variants.find((v) => String(v._id) === vid);
    if (variant) {
      if (variant.price != null) return Math.round(variant.price);
      if (product.price != null) return Math.round(product.price);
      return undefined;
    }
  }
  if (product.price != null) return Math.round(product.price);
  return undefined;
}

function catalogLineDescription({
  product,
  variantId,
}: {
  product: CatalogProduct | undefined;
  variantId?: string;
}): string {
  if (!product) return "";
  const productName = product.name?.trim() || "Untitled";
  const vid = variantId?.trim();
  if (vid && product.variants?.length) {
    const variant = product.variants.find((v) => String(v._id) === vid);
    if (variant) {
      const variantName = variant.name?.trim() || "Untitled";
      return `${productName} (${variantName})`;
    }
  }
  return productName;
}

function emptyProductLineDraft(): ProductLineDraft {
  return {
    clientKey:
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `line-${Math.random().toString(36).slice(2)}`,
    productId: "",
    variantId: "",
    description: "",
    quantity: "1",
    priceDollars: "",
  };
}

function paymentLinesToDrafts({ lines }: { lines: PurchasedProductLine[] | undefined }): ProductLineDraft[] {
  if (!lines?.length) {
    return [];
  }
  return lines.map((l) => ({
    clientKey:
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `line-${Math.random().toString(36).slice(2)}`,
    productId: typeof l.productId === "string" ? l.productId : "",
    variantId: typeof l.variantId === "string" ? l.variantId : "",
    description: typeof l.description === "string" ? l.description : "",
    quantity: String(l.quantity ?? 1),
    priceDollars:
      l.price != null && Number.isFinite(l.price) ? (l.price / 100).toFixed(2) : "",
    attendanceStatuses: l.attendanceStatuses,
  }));
}

function buildPurchasedProductsFromDrafts({
  drafts,
  productById,
}: {
  drafts: ProductLineDraft[];
  productById: Map<string, CatalogProduct>;
}):
  | { error: string }
  | {
      purchasedProducts: Array<{
        productId?: string;
        variantId?: string;
        description: string;
        quantity: number;
        price?: number;
        attendanceStatuses?: TicketAttendanceStatus[];
      }>;
    } {
  const purchasedProducts: Array<{
    productId?: string;
    variantId?: string;
    description: string;
    quantity: number;
    price?: number;
    attendanceStatuses?: TicketAttendanceStatus[];
  }> = [];

  for (let i = 0; i < drafts.length; i++) {
    const d = drafts[i]!;
    const productId = d.productId.trim();
    const variantId = d.variantId.trim();
    const description = d.description;
    const qtyStr = d.quantity.trim();
    const priceStr = d.priceDollars.trim();

    const qtyIsDefaultOnly = qtyStr === "" || qtyStr === "1";
    const allEmpty =
      !productId &&
      !variantId &&
      !description.trim() &&
      !priceStr &&
      qtyIsDefaultOnly;
    if (allEmpty) continue;

    if (qtyStr === "") {
      return { error: `Line ${i + 1}: quantity is required` };
    }
    const qtyParsed = Number.parseFloat(qtyStr);
    const quantity = Math.round(qtyParsed);
    if (!Number.isFinite(qtyParsed) || !Number.isInteger(quantity) || quantity < 1) {
      return { error: `Line ${i + 1}: quantity must be a positive integer` };
    }

    const line: {
      productId?: string;
      variantId?: string;
      description: string;
      quantity: number;
      price?: number;
      attendanceStatuses?: TicketAttendanceStatus[];
    } = { description, quantity };

    if (productId) {
      if (!OBJECT_ID_HEX_RE.test(productId)) {
        return {
          error: `Line ${i + 1}: product id must be a 24-character hex ObjectId`,
        };
      }
      line.productId = productId;
    }
    if (variantId) {
      if (!OBJECT_ID_HEX_RE.test(variantId)) {
        return {
          error: `Line ${i + 1}: variant id must be a 24-character hex ObjectId`,
        };
      }
      line.variantId = variantId;
    }
    if (priceStr !== "") {
      const amountParsed = Number.parseFloat(priceStr);
      if (!Number.isFinite(amountParsed) || amountParsed < 0) {
        return {
          error: `Line ${i + 1}: unit price must be a valid non-negative number`,
        };
      }
      line.price = Math.round(amountParsed * 100);
    }

    const picked = productId ? productById.get(productId) : undefined;
    if (isTicketCatalogProduct({ product: picked })) {
      line.attendanceStatuses = normalizeAttendanceStatuses({
        statuses: d.attendanceStatuses,
        quantity,
      });
    }

    purchasedProducts.push(line);
  }

  return { purchasedProducts };
}

type OrderFormSectionId = "contact" | "shipping" | "internal";

function productLineCollapsedLabel({
  row,
  product,
  lineIndex,
}: {
  row: ProductLineDraft;
  product: CatalogProduct | undefined;
  lineIndex: number;
}): string {
  const desc = row.description.trim();
  if (desc) return desc;
  const fromCatalog = catalogLineDescription({ product, variantId: row.variantId });
  if (fromCatalog) return fromCatalog;
  const pid = row.productId.trim();
  if (pid) return `Product ${pid.slice(0, 6)}…`;
  return `Line ${lineIndex + 1}`;
}

function productLineUnitPriceCents({
  row,
  product,
}: {
  row: ProductLineDraft;
  product: CatalogProduct | undefined;
}): number | undefined {
  const priceStr = row.priceDollars.trim();
  if (priceStr !== "") {
    const amountParsed = Number.parseFloat(priceStr);
    if (Number.isFinite(amountParsed) && amountParsed >= 0) {
      return Math.round(amountParsed * 100);
    }
  }
  return catalogUnitPriceCents({ product, variantId: row.variantId });
}

function lineItemsDraftSnapshot(drafts: ProductLineDraft[]): string {
  return JSON.stringify(
    drafts.map((d) => ({
      productId: d.productId.trim(),
      variantId: d.variantId.trim(),
      description: d.description.trim(),
      quantity: d.quantity.trim(),
      priceDollars: d.priceDollars.trim(),
      attendanceStatuses: d.attendanceStatuses,
    })),
  );
}

function orderAmountCentsFromProductDrafts({
  drafts,
  productById,
  shippingCostDollars,
}: {
  drafts: ProductLineDraft[];
  productById: Map<string, CatalogProduct>;
  shippingCostDollars: string;
}): number {
  let totalCents = 0;

  for (const row of drafts) {
    const productId = row.productId.trim();
    const variantId = row.variantId.trim();
    const description = row.description;
    const qtyStr = row.quantity.trim();
    const priceStr = row.priceDollars.trim();

    const qtyIsDefaultOnly = qtyStr === "" || qtyStr === "1";
    const allEmpty =
      !productId &&
      !variantId &&
      !description.trim() &&
      !priceStr &&
      qtyIsDefaultOnly;
    if (allEmpty) continue;

    const qtyParsed = Number.parseFloat(qtyStr === "" ? "1" : qtyStr);
    const quantity = Math.round(qtyParsed);
    if (!Number.isFinite(qtyParsed) || !Number.isInteger(quantity) || quantity < 1) {
      continue;
    }

    const product = productId ? productById.get(productId) : undefined;
    const unitCents = productLineUnitPriceCents({ row, product });
    if (unitCents != null) {
      totalCents += unitCents * quantity;
    }
  }

  const shippingStr = shippingCostDollars.trim();
  if (shippingStr !== "") {
    const shippingParsed = Number.parseFloat(shippingStr);
    if (Number.isFinite(shippingParsed) && shippingParsed >= 0) {
      totalCents += Math.round(shippingParsed * 100);
    }
  }

  return totalCents;
}

function formatUnitPriceForHeader({
  cents,
  currency,
}: {
  cents: number;
  currency: string;
}): string {
  const amount = formatCentsAsDollarInput({ cents });
  if (currency === "usd") return `$${amount}`;
  return `${amount} ${currency.toUpperCase()}`;
}

function CollapsibleOrderSection({
  title,
  titleExtra,
  expanded,
  onToggle,
  headerEnd,
  contentClassName = "space-y-4 border-t px-4 pb-4 pt-4",
  collapsible = true,
  children,
}: {
  title: string;
  titleExtra?: ReactNode;
  expanded?: boolean;
  onToggle?: () => void;
  headerEnd?: ReactNode;
  contentClassName?: string;
  collapsible?: boolean;
  children: ReactNode;
}) {
  const isExpanded = collapsible ? (expanded ?? true) : true;
  const titleContent = (
    <>
      {collapsible ? (
        isExpanded ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )
      ) : null}
      <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
      {titleExtra}
    </>
  );

  return (
    <div className="rounded-lg border">
      <div className="flex items-center justify-between gap-2 p-4">
        {collapsible ? (
          <button
            type="button"
            onClick={onToggle}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            {titleContent}
          </button>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-2">{titleContent}</div>
        )}
        {headerEnd}
      </div>
      {isExpanded && children ? (
        <div className={contentClassName}>{children}</div>
      ) : null}
    </div>
  );
}

export interface OrderPayment {
  _id: string;
  lead?: string;
  customerEmail?: string;
  customerPhone?: string;
  amount: number;
  currency?: string;
  status?: PaymentStatus;
  paymentIntentId?: string;
  fulfillmentStatus?: PaymentFulfillmentStatus | null;
  note?: string | null;
  easyship?: {
    shipments?: { shipmentId: string; labelState: string }[];
  };
  purchasedProducts?: PurchasedProductLine[];
  shipping?: {
    name?: string;
    address?: {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      country?: string;
      postal_code?: string;
    };
    chargedOption?: {
      name?: string;
      cost?: number;
    };
  };
}

export interface OrderFormProps {
  mode: "create" | "edit";
  payment: OrderPayment | null;
  patchPayment?: (patch: Partial<OrderPayment>) => void;
  onSaved: (payment?: OrderPayment) => void;
}

export function OrderForm({
  mode,
  payment,
  patchPayment,
  onSaved,
}: OrderFormProps) {
  const navigate = useNavigate();
  const [easyShipConnected, setEasyShipConnected] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncingEasyShip, setIsSyncingEasyShip] = useState(false);
  const [isSendingShipmentEmail, setIsSendingShipmentEmail] = useState(false);
  const initializedForPaymentIdRef = useRef<string | null>(null);
  const lineItemsBaselineRef = useRef<string | null>(null);
  const lineItemsSyncedWithPaymentRef = useRef(false);

  const [fulfillmentStatus, setFulfillmentStatus] =
    useState<PaymentFulfillmentStatus>("unfulfilled");
  const [statusDraft, setStatusDraft] = useState<PaymentStatus>("open");
  const [note, setNote] = useState("");
  const [shippingName, setShippingName] = useState("");
  const [shippingLine1, setShippingLine1] = useState("");
  const [shippingLine2, setShippingLine2] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingState, setShippingState] = useState("");
  const [shippingPostal, setShippingPostal] = useState("");
  const [shippingCountry, setShippingCountry] = useState("");
  const [shippingOptionName, setShippingOptionName] = useState("");
  const [shippingOptionCost, setShippingOptionCost] = useState("");
  const [customerEmailDraft, setCustomerEmailDraft] = useState("");
  const [customerPhoneDraft, setCustomerPhoneDraft] = useState("");
  const [amountDollars, setAmountDollars] = useState("");
  const [currencyDraft, setCurrencyDraft] = useState("usd");
  const initialCreateLineRef = useRef<ProductLineDraft | null>(
    mode === "create" ? emptyProductLineDraft() : null,
  );
  const [productLineDrafts, setProductLineDrafts] = useState<ProductLineDraft[]>(() =>
    initialCreateLineRef.current ? [initialCreateLineRef.current] : [],
  );
  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([]);
  const [stripeAccountCurrency, setStripeAccountCurrency] = useState<string | null>(null);
  const [isLinkingLead, setIsLinkingLead] = useState(false);
  const [createLeadId, setCreateLeadId] = useState<string | undefined>(undefined);
  const [createSelectedLead, setCreateSelectedLead] = useState<ContactLeadOption | null>(
    null,
  );
  const [expandedSections, setExpandedSections] = useState<Set<OrderFormSectionId>>(
    () => new Set(),
  );
  const [expandedLineKeys, setExpandedLineKeys] = useState<Set<string>>(() =>
    initialCreateLineRef.current ?
      new Set([initialCreateLineRef.current.clientKey])
    : new Set(),
  );

  const toggleSection = (id: OrderFormSectionId) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleLine = (clientKey: string) => {
    setExpandedLineKeys((prev) => {
      if (prev.has(clientKey)) return new Set();
      return new Set([clientKey]);
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch("/api/products");
        if (!response.ok || cancelled) return;
        const data = await response.json();
        setCatalogProducts((data.products || []) as CatalogProduct[]);
      } catch {
        if (!cancelled) setCatalogProducts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch("/api/stripe-account");
        if (!response.ok || cancelled) return;
        const data = await response.json();
        const currency = data.stripeAccount?.currency?.trim()?.toLowerCase();
        if (currency) setStripeAccountCurrency(currency);
      } catch {
        if (!cancelled) setStripeAccountCurrency(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    async function fetchEasyShipStatus() {
      try {
        const response = await fetch("/api/easyship/status");
        if (!response.ok) return;
        const data = await response.json();
        setEasyShipConnected(Boolean(data.connected));
      } catch {
        setEasyShipConnected(false);
      }
    }
    fetchEasyShipStatus();
  }, []);

  useEffect(() => {
    if (mode !== "edit" || !payment) return;
    if (initializedForPaymentIdRef.current === payment._id) return;
    initializedForPaymentIdRef.current = payment._id;
    lineItemsSyncedWithPaymentRef.current = false;

    setFulfillmentStatus(payment.fulfillmentStatus ?? "unfulfilled");
    setStatusDraft(payment.status ?? "open");
    setNote(payment.note ?? "");
    setShippingName(payment.shipping?.name ?? "");
    setShippingLine1(payment.shipping?.address?.line1 ?? "");
    setShippingLine2(payment.shipping?.address?.line2 ?? "");
    setShippingCity(payment.shipping?.address?.city ?? "");
    setShippingState(payment.shipping?.address?.state ?? "");
    setShippingPostal(payment.shipping?.address?.postal_code ?? "");
    setShippingCountry(payment.shipping?.address?.country ?? "");
    setShippingOptionName(payment.shipping?.chargedOption?.name ?? "");
    setShippingOptionCost(
      payment.shipping?.chargedOption?.cost != null
        ? (payment.shipping.chargedOption.cost / 100).toFixed(2)
        : "",
    );
    setCustomerEmailDraft(payment.customerEmail ?? "");
    setCustomerPhoneDraft(payment.customerPhone ?? "");
    setAmountDollars(
      typeof payment.amount === "number" && Number.isFinite(payment.amount)
        ? (payment.amount / 100).toFixed(2)
        : "",
    );
    setCurrencyDraft(payment.currency?.trim() || "usd");
    const lineDrafts = paymentLinesToDrafts({ lines: payment.purchasedProducts });
    setProductLineDrafts(lineDrafts);
    lineItemsBaselineRef.current = lineItemsDraftSnapshot(lineDrafts);
  }, [mode, payment]);

  useEffect(() => {
    if (mode !== "create") return;
    initializedForPaymentIdRef.current = null;
    lineItemsBaselineRef.current = null;
    lineItemsSyncedWithPaymentRef.current = false;
    setFulfillmentStatus("unfulfilled");
    setStatusDraft("open");
    setNote("");
    setShippingName("");
    setShippingLine1("");
    setShippingLine2("");
    setShippingCity("");
    setShippingState("");
    setShippingPostal("");
    setShippingCountry("");
    setShippingOptionName("");
    setShippingOptionCost("");
    setCustomerEmailDraft("");
    setCustomerPhoneDraft("");
    setAmountDollars("");
    setCurrencyDraft(stripeAccountCurrency ?? "usd");
    const defaultLine = emptyProductLineDraft();
    setProductLineDrafts([defaultLine]);
    setExpandedLineKeys(new Set([defaultLine.clientKey]));
  }, [mode]);

  useEffect(() => {
    if (mode !== "create" || !stripeAccountCurrency) return;
    setCurrencyDraft((prev) => (prev === "usd" ? stripeAccountCurrency : prev));
  }, [mode, stripeAccountCurrency]);

  const buildShippingPayload = () => {
    const optName = shippingOptionName.trim();
    const optCostRaw = shippingOptionCost.trim();
    let chargedOption: { name: string; cost: number } | undefined;
    if (optName !== "" || optCostRaw !== "") {
      if (!optName || optCostRaw === "") {
        return {
          error: "Shipping option needs both a label and an amount",
        };
      }
      const amountParsed = Number.parseFloat(optCostRaw);
      if (!Number.isFinite(amountParsed) || amountParsed < 0) {
        return {
          error: "Shipping option amount must be a valid non-negative number",
        };
      }
      chargedOption = { name: optName, cost: Math.round(amountParsed * 100) };
    }

    const shippingPayload = {
      name: shippingName,
      address: {
        line1: shippingLine1,
        ...(shippingLine2.trim() !== "" ? { line2: shippingLine2.trim() } : {}),
        city: shippingCity,
        state: shippingState,
        country: shippingCountry,
        postal_code: shippingPostal,
      },
      ...(chargedOption ? { chargedOption } : {}),
    };

    return { shippingPayload };
  };

  const addProductLineDraft = () => {
    const draft = emptyProductLineDraft();
    setProductLineDrafts((prev) => [...prev, draft]);
    setExpandedLineKeys(new Set([draft.clientKey]));
  };

  const removeProductLineDraft = ({ index }: { index: number }) => {
    setProductLineDrafts((prev) => prev.filter((_, i) => i !== index));
  };

  const productsForPicker = useMemo(() => {
    const map = new Map<string, CatalogProduct>(
      catalogProducts.map((p) => [String(p._id), { ...p, _id: String(p._id) }]),
    );
    for (const d of productLineDrafts) {
      const pid = d.productId.trim();
      if (pid && !map.has(pid)) {
        map.set(pid, {
          _id: pid,
          name: `Unknown product (${pid.slice(0, 6)}…)`,
          variants: [],
        });
      }
    }
    const { active, archived } = partitionProductCatalogOptions([...map.values()]);
    return [...active, ...archived];
  }, [catalogProducts, productLineDrafts]);

  const productByIdPicker = useMemo(
    () => new Map(productsForPicker.map((p) => [p._id, p])),
    [productsForPicker],
  );

  useEffect(() => {
    if (mode === "edit") {
      const baseline = lineItemsBaselineRef.current;
      if (baseline == null) return;

      const snapshot = lineItemsDraftSnapshot(productLineDrafts);
      if (snapshot === baseline) {
        lineItemsSyncedWithPaymentRef.current = true;
        return;
      }

      if (!lineItemsSyncedWithPaymentRef.current) return;
    }

    const cents = orderAmountCentsFromProductDrafts({
      drafts: productLineDrafts,
      productById: productByIdPicker,
      shippingCostDollars: shippingOptionCost,
    });
    setAmountDollars(formatCentsAsDollarInput({ cents }));
    if (mode === "edit") {
      patchPayment?.({ amount: cents });
    }
  }, [mode, productLineDrafts, productByIdPicker, shippingOptionCost]);

  const updateProductLineDraft = ({
    index,
    field,
    value,
  }: {
    index: number;
    field: keyof ProductLineDraft;
    value: string;
  }) => {
    setProductLineDrafts((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const next: ProductLineDraft = { ...row, [field]: value };
        if (field === "quantity") {
          const picked = row.productId.trim()
            ? productByIdPicker.get(row.productId.trim())
            : undefined;
          if (isTicketCatalogProduct({ product: picked })) {
            next.attendanceStatuses = normalizeAttendanceStatuses({
              statuses: row.attendanceStatuses,
              quantity: ticketQuantityFromDraft({ draft: next }),
            });
          }
        }
        if (field === "variantId") {
          const picked = row.productId.trim()
            ? productByIdPicker.get(row.productId.trim())
            : undefined;
          const cents = catalogUnitPriceCents({ product: picked, variantId: value });
          if (cents != null) {
            next.priceDollars = formatCentsAsDollarInput({ cents });
          }
          next.description = catalogLineDescription({ product: picked, variantId: value });
        }
        return next;
      }),
    );
  };

  const updateTicketAttendanceStatus = ({
    lineIndex,
    ticketIndex,
    status,
  }: {
    lineIndex: number;
    ticketIndex: number;
    status: TicketAttendanceStatus;
  }) => {
    setProductLineDrafts((prev) =>
      prev.map((row, i) => {
        if (i !== lineIndex) return row;
        const statuses = normalizeAttendanceStatuses({
          statuses: row.attendanceStatuses,
          quantity: ticketQuantityFromDraft({ draft: row }),
        });
        statuses[ticketIndex] = status;
        return { ...row, attendanceStatuses: statuses };
      }),
    );
  };

  const updateProductLineProductId = ({
    index,
    productId,
  }: {
    index: number;
    productId: string;
  }) => {
    setProductLineDrafts((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const picked = productId ? productByIdPicker.get(productId) : undefined;
        const next: ProductLineDraft = { ...row, productId, variantId: "" };
        const cents = catalogUnitPriceCents({ product: picked });
        if (cents != null) {
          next.priceDollars = formatCentsAsDollarInput({ cents });
        }
        next.description = catalogLineDescription({ product: picked });
        if (isTicketCatalogProduct({ product: picked })) {
          next.attendanceStatuses = normalizeAttendanceStatuses({
            statuses: row.attendanceStatuses,
            quantity: ticketQuantityFromDraft({ draft: next }),
          });
        } else {
          delete next.attendanceStatuses;
        }
        return next;
      }),
    );
  };

  const isStatusLocked =
    mode === "edit" &&
    payment?.status === "complete" &&
    typeof payment.paymentIntentId === "string" &&
    payment.paymentIntentId.trim() !== "";

  const handleSave = async (options?: {
    skipOnSaved?: boolean;
  }): Promise<OrderPayment | null> => {
    const shippingResult = buildShippingPayload();
    if ("error" in shippingResult) {
      toast.error(shippingResult.error);
      return null;
    }
    const { shippingPayload } = shippingResult;

    if (mode === "create") {
      const purchasedResult = buildPurchasedProductsFromDrafts({
        drafts: productLineDrafts,
        productById: productByIdPicker,
      });
      if ("error" in purchasedResult) {
        toast.error(purchasedResult.error);
        return null;
      }
      const amountParsed = Number.parseFloat(amountDollars.trim());
      if (!Number.isFinite(amountParsed) || amountParsed < 0) {
        toast.error("Enter a valid non-negative order amount");
        return null;
      }
      const cents = Math.round(amountParsed * 100);
      setIsSaving(true);
      try {
        const response = await fetch("/api/payments", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: cents,
            currency: currencyDraft.trim().toLowerCase() || "usd",
            ...(customerEmailDraft.trim()
              ? { customerEmail: customerEmailDraft.trim() }
              : {}),
            ...(customerPhoneDraft.trim()
              ? { customerPhone: customerPhoneDraft.trim() }
              : {}),
            ...(createLeadId ? { lead: createLeadId } : {}),
            status: statusDraft,
            fulfillmentStatus,
            note,
            shipping: shippingPayload,
            ...(purchasedResult.purchasedProducts.length > 0
              ? { purchasedProducts: purchasedResult.purchasedProducts }
              : {}),
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          toast.error(typeof data.error === "string" ? data.error : "Could not create order");
          return null;
        }
        toast.success("Order created");
        const createdPayment = data.payment as OrderPayment | undefined;
        if (!options?.skipOnSaved) {
          onSaved(createdPayment);
        }
        return createdPayment ?? null;
      } catch (e) {
        console.error(e);
        toast.error("Could not create order");
        return null;
      } finally {
        setIsSaving(false);
      }
    }

    if (!payment?._id) return null;

    const purchasedResult = buildPurchasedProductsFromDrafts({
      drafts: productLineDrafts,
      productById: productByIdPicker,
    });
    if ("error" in purchasedResult) {
      toast.error(purchasedResult.error);
      return null;
    }

    const amountParsed = Number.parseFloat(amountDollars.trim());
    if (!Number.isFinite(amountParsed) || amountParsed < 0) {
      toast.error("Enter a valid non-negative order amount");
      return null;
    }
    const cents = Math.round(amountParsed * 100);

    setIsSaving(true);
    try {
      const response = await fetch(`/api/payments/${payment._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: cents,
          currency: currencyDraft.trim().toLowerCase() || "usd",
          customerEmail: customerEmailDraft.trim(),
          customerPhone: customerPhoneDraft.trim(),
          ...(isStatusLocked ? {} : { status: statusDraft }),
          fulfillmentStatus,
          note,
          shipping: shippingPayload,
          purchasedProducts: purchasedResult.purchasedProducts,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(typeof data.error === "string" ? data.error : "Could not save order");
        return null;
      }
      toast.success("Order updated");
      patchPayment?.({ status: isStatusLocked ? payment.status : statusDraft });
      if (!options?.skipOnSaved) {
        onSaved();
      }
      return payment;
    } catch (e) {
      console.error(e);
      toast.error("Could not save order");
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const canCollect =
    mode === "create" ?
      statusDraft !== "complete"
    : Boolean(payment && payment.status !== "complete");

  const handleCollect = async () => {
    const savedPayment = await handleSave({ skipOnSaved: true });
    if (savedPayment?._id) {
      navigate(`/sales/orders/${savedPayment._id}/collect`);
    }
  };

  const handleSyncEasyShip = async () => {
    if (!payment?._id) return;
    const hasExistingEasyShip =
      (payment.easyship?.shipments?.length ?? 0) > 0;
    if (
      hasExistingEasyShip &&
      !confirm(
        "This will delete the existing Easyship shipment(s) for this order and create new ones. Continue?",
      )
    ) {
      return;
    }
    setIsSyncingEasyShip(true);
    try {
      const response = await fetch("/api/easyship/sync-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentId: payment._id,
          ...(hasExistingEasyShip ? { resync: true } : {}),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(typeof data.error === "string" ? data.error : "Could not sync to Easyship");
        return;
      }
      const idsRaw = data.easyShipmentIds;
      const ids = Array.isArray(idsRaw)
        ? idsRaw.filter((x): x is string => typeof x === "string" && x.trim().length > 0)
        : [];
      const easyshipPayload = data.easyship;
      const easyshipNormalized =
        easyshipPayload &&
        typeof easyshipPayload === "object" &&
        Array.isArray((easyshipPayload as { shipments?: unknown }).shipments) ?
          (easyshipPayload as OrderPayment["easyship"])
        : ids.length > 0 ?
          {
            shipments: ids.map((shipmentId) => ({
              shipmentId,
              labelState: "unknown",
            })),
          }
        : undefined;
      toast.success(
        ids.length > 1
          ? `Easyship: ${ids.length} shipments (${ids.join(", ")})`
          : ids.length === 1
            ? `Easyship shipment created (${ids[0]})`
            : "Easyship shipment created",
      );
      if (easyshipNormalized?.shipments?.length) {
        patchPayment?.({ easyship: easyshipNormalized });
      }
    } catch (e) {
      console.error(e);
      toast.error("Could not sync to Easyship");
    } finally {
      setIsSyncingEasyShip(false);
    }
  };

  const handleSendShipmentEmail = async () => {
    if (!payment?._id) return;
    setIsSendingShipmentEmail(true);
    try {
      const response = await fetch(`/api/payments/${payment._id}/send-shipment-email`, {
        method: "POST",
      });
      const data = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) {
        toast.error(
          typeof data.error === "string" ? data.error : "Could not send shipment email",
        );
        return;
      }
      setFulfillmentStatus("shipped");
      patchPayment?.({ fulfillmentStatus: "shipped" });
      toast.success("Shipment email sent");
    } catch {
      toast.error("Could not send shipment email");
    } finally {
      setIsSendingShipmentEmail(false);
    }
  };

  const resolvedCurrency = currencyDraft.trim().toLowerCase() || "usd";

  const totalHeaderAmount = useMemo(() => {
    const amountParsed = Number.parseFloat(amountDollars.trim());
    if (!Number.isFinite(amountParsed) || amountParsed < 0) return null;
    return formatUnitPriceForHeader({
      cents: Math.round(amountParsed * 100),
      currency: resolvedCurrency,
    });
  }, [amountDollars, resolvedCurrency]);

  const paymentLeadId =
    payment?.lead != null && String(payment.lead).trim() !== "" ?
      String(payment.lead)
    : undefined;

  const patchPaymentLead = async ({
    leadId,
    lead,
  }: {
    leadId: string | null;
    lead?: ContactLeadOption;
  }) => {
    if (!payment?._id) return;
    setIsLinkingLead(true);
    try {
      const body: Record<string, string | null> = { lead: leadId };
      const localPatch: Partial<OrderPayment> = {};
      if (leadId) {
        localPatch.lead = leadId;
      } else {
        localPatch.lead = undefined;
      }

      if (lead) {
        if (!payment.customerEmail?.trim() && lead.email?.trim()) {
          body.customerEmail = lead.email.trim();
          localPatch.customerEmail = lead.email.trim();
          setCustomerEmailDraft(lead.email.trim());
        }
        if (!payment.customerPhone?.trim() && lead.phoneNumber?.trim()) {
          body.customerPhone = lead.phoneNumber.trim();
          localPatch.customerPhone = lead.phoneNumber.trim();
          setCustomerPhoneDraft(lead.phoneNumber.trim());
        }
      }

      const response = await fetch(`/api/payments/${payment._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(typeof data.error === "string" ? data.error : "Could not update contact");
        return;
      }
      patchPayment?.(localPatch);
      toast.success(leadId ? "Contact linked" : "Contact cleared");
    } catch (e) {
      console.error(e);
      toast.error("Could not update contact");
    } finally {
      setIsLinkingLead(false);
    }
  };

  const handleLeadSelect = (lead: ContactLeadOption) => {
    void patchPaymentLead({ leadId: lead._id, lead });
  };

  const handleCreateLeadSelect = (lead: ContactLeadOption) => {
    setCreateLeadId(lead._id);
    setCreateSelectedLead(lead);
    if (!customerEmailDraft.trim() && lead.email?.trim()) {
      setCustomerEmailDraft(lead.email.trim());
    }
    if (!customerPhoneDraft.trim() && lead.phoneNumber?.trim()) {
      setCustomerPhoneDraft(lead.phoneNumber.trim());
    }
  };

  return (
    <div className="py-4 space-y-6 w-full">
      {(mode === "create" || payment) && (
        <CollapsibleOrderSection
          title="Items"
          collapsible={false}
          contentClassName="border-t"
          headerEnd={
            <Button type="button" variant="outline" size="sm" onClick={addProductLineDraft}>
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          }
        >
          {productLineDrafts.length > 0 ? (
          <div>
            {productLineDrafts.map((row, idx) => {
              const pid = row.productId.trim();
              const picked = pid ? productByIdPicker.get(pid) : undefined;
              const catalogVariants = picked?.variants ?? [];
              const showVariantSelect = catalogVariants.length > 0;
              const vid = row.variantId.trim();
              const variantById = new Map(
                catalogVariants.map((v) => [String(v._id), v]),
              );
              const variantOptions = [...catalogVariants];
              if (vid && !variantById.has(vid)) {
                variantOptions.push({
                  _id: vid,
                  name: `Unknown variant (${vid.slice(0, 6)}…)`,
                });
              }
              const lineExpanded = expandedLineKeys.has(row.clientKey);
              return (
                <div
                  key={row.clientKey}
                  className="border-b border-border/60 last:border-b-0"
                >
                  <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={lineExpanded}
                    onClick={() => toggleLine(row.clientKey)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleLine(row.clientKey);
                      }
                    }}
                    className={cn(
                      "flex cursor-pointer items-center justify-between gap-2 px-3 py-2.5 text-left transition-colors hover:bg-muted/60",
                      lineExpanded && "bg-muted/60",
                    )}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span
                        className={cn(
                          "truncate text-sm",
                          lineExpanded ? "font-semibold" : "font-medium",
                        )}
                      >
                        {productLineCollapsedLabel({ row, product: picked, lineIndex: idx })}
                      </span>
                      {(() => {
                        const unitCents = productLineUnitPriceCents({ row, product: picked });
                        if (unitCents == null) return null;
                        return (
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {formatUnitPriceForHeader({
                              cents: unitCents,
                              currency: resolvedCurrency,
                            })}
                          </span>
                        );
                      })()}
                      <span className="shrink-0 text-xs text-muted-foreground">
                        × {row.quantity.trim() || "1"}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeProductLineDraft({ index: idx });
                      }}
                      aria-label={`Remove line ${idx + 1}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {lineExpanded ? (
                  <div className="space-y-3 border-t border-border/60 px-3 pb-3 pt-3">
                  <div className="space-y-2">
                    <Label htmlFor={`order-line-${idx}-product`}>Product</Label>
                    <ProductCatalogPicker
                      id={`order-line-${idx}-product`}
                      value={pid}
                      products={productsForPicker}
                      onSelect={(productId) =>
                        updateProductLineProductId({ index: idx, productId })
                      }
                    />
                  </div>
                  {showVariantSelect && (
                    <div className="space-y-2">
                      <Label htmlFor={`order-line-${idx}-variant`}>Variant</Label>
                      <Select
                        value={vid === "" ? SELECT_NONE : vid}
                        onValueChange={(v) =>
                          updateProductLineDraft({
                            index: idx,
                            field: "variantId",
                            value: v === SELECT_NONE ? "" : v,
                          })
                        }
                      >
                        <SelectTrigger id={`order-line-${idx}-variant`} className="w-full">
                          <SelectValue placeholder="None" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={SELECT_NONE}>None</SelectItem>
                          {variantOptions.map((v) => (
                            <SelectItem key={String(v._id)} value={String(v._id)}>
                              {v.name?.trim() || "Untitled"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor={`order-line-${idx}-desc`}>Description</Label>
                    <Input
                      id={`order-line-${idx}-desc`}
                      value={row.description}
                      onChange={(e) =>
                        updateProductLineDraft({
                          index: idx,
                          field: "description",
                          value: e.target.value,
                        })
                      }
                      placeholder="Product name or note"
                    />
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-2 w-24">
                      <Label htmlFor={`order-line-${idx}-qty`}>Qty</Label>
                      <Input
                        id={`order-line-${idx}-qty`}
                        inputMode="numeric"
                        value={row.quantity}
                        onChange={(e) =>
                          updateProductLineDraft({
                            index: idx,
                            field: "quantity",
                            value: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2 flex-1 min-w-[8rem]">
                      <Label htmlFor={`order-line-${idx}-price`}>
                        Unit price ({resolvedCurrency.toUpperCase()})
                      </Label>
                      <Input
                        id={`order-line-${idx}-price`}
                        inputMode="decimal"
                        value={row.priceDollars}
                        onChange={(e) =>
                          updateProductLineDraft({
                            index: idx,
                            field: "priceDollars",
                            value: e.target.value,
                          })
                        }
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                  {mode === "edit" &&
                    isTicketCatalogProduct({ product: picked }) &&
                    row.quantity.trim() !== "" && (
                      <div className="space-y-2 border-t border-border/60 pt-3">
                        <p className="text-sm font-medium">Ticket attendance</p>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {normalizeAttendanceStatuses({
                            statuses: row.attendanceStatuses,
                            quantity: ticketQuantityFromDraft({ draft: row }),
                          }).map((status, ticketIdx) => (
                            <div key={ticketIdx} className="space-y-1">
                              <Label htmlFor={`order-line-${idx}-ticket-${ticketIdx}`}>
                                Ticket {ticketIdx + 1}
                              </Label>
                              <Select
                                value={status}
                                onValueChange={(v) =>
                                  updateTicketAttendanceStatus({
                                    lineIndex: idx,
                                    ticketIndex: ticketIdx,
                                    status: v as TicketAttendanceStatus,
                                  })
                                }
                              >
                                <SelectTrigger id={`order-line-${idx}-ticket-${ticketIdx}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {ticketAttendanceSelectOptions.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          ) : null}
        </CollapsibleOrderSection>
      )}

      {(mode === "create" || payment) && (
        <CollapsibleOrderSection
          title="Payment"
          collapsible={false}
          titleExtra={
            <span className="flex items-center gap-3">
              {payment?.paymentIntentId?.trim() ? (
                <a
                  href={`https://dashboard.stripe.com/payments/${payment.paymentIntentId.trim()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-normal text-primary hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Stripe
                </a>
              ) : null}
              {totalHeaderAmount ? (
                <span className="text-sm font-normal text-muted-foreground">{totalHeaderAmount}</span>
              ) : null}
            </span>
          }
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="order-amount">Amount</Label>
              <Input
                id="order-amount"
                inputMode="decimal"
                value={amountDollars}
                onChange={(e) => setAmountDollars(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="order-currency">Currency</Label>
              <Input
                id="order-currency"
                value={currencyDraft}
                onChange={(e) => setCurrencyDraft(e.target.value)}
                placeholder={stripeAccountCurrency ?? "usd"}
                autoComplete="transaction-currency"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="order-status">Status</Label>
            <Select
              value={statusDraft}
              onValueChange={(v) => setStatusDraft(v as PaymentStatus)}
              disabled={isStatusLocked}
            >
              <SelectTrigger id="order-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {paymentStatusSelectOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isStatusLocked && (
              <p className="text-xs text-muted-foreground">
                Status cannot be changed on a completed order with a Stripe payment.
              </p>
            )}
          </div>
        </CollapsibleOrderSection>
      )}

      {(mode === "create" || payment) && (
        <CollapsibleOrderSection
          title="Contact"
          expanded={expandedSections.has("contact")}
          onToggle={() => toggleSection("contact")}
        >
          <div className="space-y-3">
            {mode === "create" ? (
              <ContactLeadPicker
                value={createLeadId}
                selectedLead={createSelectedLead}
                onSelect={handleCreateLeadSelect}
              />
            ) : payment ? (
              <ContactLeadPicker
                value={paymentLeadId}
                onSelect={handleLeadSelect}
                disabled={isLinkingLead}
              />
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="order-email">Customer email</Label>
              <Input
                id="order-email"
                type="email"
                value={customerEmailDraft}
                onChange={(e) => setCustomerEmailDraft(e.target.value)}
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="order-phone">Customer phone</Label>
              <Input
                id="order-phone"
                type="tel"
                value={customerPhoneDraft}
                onChange={(e) => setCustomerPhoneDraft(e.target.value)}
                autoComplete="tel"
              />
            </div>
          </div>
        </CollapsibleOrderSection>
      )}

      <CollapsibleOrderSection
        title="Shipping"
        expanded={expandedSections.has("shipping")}
        onToggle={() => toggleSection("shipping")}
      >
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="ship-name">Recipient name</Label>
            <Input
              id="ship-name"
              value={shippingName}
              onChange={(e) => setShippingName(e.target.value)}
              autoComplete="name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ship-line1">Address line 1</Label>
            <Input
              id="ship-line1"
              value={shippingLine1}
              onChange={(e) => setShippingLine1(e.target.value)}
              autoComplete="address-line1"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ship-line2">Address line 2</Label>
            <Input
              id="ship-line2"
              value={shippingLine2}
              onChange={(e) => setShippingLine2(e.target.value)}
              autoComplete="address-line2"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="ship-city">City</Label>
              <Input
                id="ship-city"
                value={shippingCity}
                onChange={(e) => setShippingCity(e.target.value)}
                autoComplete="address-level2"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ship-state">State / region</Label>
              <Input
                id="ship-state"
                value={shippingState}
                onChange={(e) => setShippingState(e.target.value)}
                autoComplete="address-level1"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="ship-postal">Postal code</Label>
              <Input
                id="ship-postal"
                value={shippingPostal}
                onChange={(e) => setShippingPostal(e.target.value)}
                autoComplete="postal-code"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ship-country">Country</Label>
              <Input
                id="ship-country"
                value={shippingCountry}
                onChange={(e) => setShippingCountry(e.target.value)}
                autoComplete="country-name"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="space-y-2">
              <Label htmlFor="ship-opt-name">Shipping option label</Label>
              <Input
                id="ship-opt-name"
                value={shippingOptionName}
                onChange={(e) => setShippingOptionName(e.target.value)}
                placeholder="e.g. Standard"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ship-opt-cost">
                Shipping option amount ({resolvedCurrency.toUpperCase()})
              </Label>
              <Input
                id="ship-opt-cost"
                inputMode="decimal"
                value={shippingOptionCost}
                onChange={(e) => setShippingOptionCost(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Leave option label and amount blank to clear the charged shipping line on this order.
          </p>
        </div>

        <div className="space-y-2 border-t border-border/60 pt-4">
          <Label htmlFor="order-fulfillment">Fulfillment status</Label>
          <Select
            value={fulfillmentStatus}
            onValueChange={(v) => setFulfillmentStatus(v as PaymentFulfillmentStatus)}
          >
            <SelectTrigger id="order-fulfillment">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {paymentFulfillmentSelectOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {easyShipConnected && mode === "edit" && payment && (
          <div className="space-y-3 border-t border-border/60 pt-4">
            <div>
              <h3 className="text-sm font-semibold tracking-tight">Easyship</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Sends Easyship&apos;s{" "}
                <a
                  className="text-primary underline underline-offset-2"
                  href="https://developers.easyship.com/reference/shipments_create"
                  target="_blank"
                  rel="noreferrer"
                >
                  Create shipment
                </a>{" "}
                request using this order&apos;s ship-to details and catalog lines. Re-sync deletes
                existing shipments{" "}
                <a
                  className="text-primary underline underline-offset-2"
                  href="https://developers.easyship.com/reference/shipments_delete"
                  target="_blank"
                  rel="noreferrer"
                >
                  via the API
                </a>{" "}
                then creates replacements.
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={handleSyncEasyShip}
              disabled={
                isSyncingEasyShip ||
                payment.fulfillmentStatus === "fulfilled" ||
                !shippingLine1.trim() ||
                !payment.customerEmail?.trim()
              }
            >
              {isSyncingEasyShip ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  Syncing…
                </>
              ) : payment.easyship?.shipments?.length ? (
                "Re-sync Easyship"
              ) : (
                "Sync to Easyship"
              )}
            </Button>
            {(() => {
              const lines =
                payment.easyship?.shipments?.filter(
                  (s) => typeof s.shipmentId === "string" && s.shipmentId.trim(),
                ) ?? [];
              return lines.length > 0 ? (
                <p className="text-xs text-muted-foreground font-mono">
                  Synced:{" "}
                  {lines
                    .map((s) => `${s.shipmentId} (${s.labelState})`)
                    .join(", ")}
                </p>
              ) : (
                (!shippingLine1.trim() || !payment.customerEmail?.trim()) && (
                  <p className="text-xs text-muted-foreground">
                    Sync needs a shipping address and customer email on the order.
                  </p>
                )
              );
            })()}
          </div>
        )}

        {mode === "edit" && payment && (
          <div className="flex flex-wrap gap-2 border-t border-border/60 pt-4">
            <Button type="button" variant="outline" asChild>
              <Link to={`/orders/${payment._id}/shipment/email/preview`}>
                Preview Shipment Email
              </Link>
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={
                isSendingShipmentEmail ||
                payment.fulfillmentStatus === "shipped" ||
                !payment.easyship?.shipments?.some(
                  (s) => typeof s.shipmentId === "string" && s.shipmentId.trim(),
                )
              }
              onClick={() => void handleSendShipmentEmail()}
            >
              {isSendingShipmentEmail ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  Sending…
                </>
              ) : (
                "Send Shipment Email"
              )}
            </Button>
          </div>
        )}
      </CollapsibleOrderSection>

      <CollapsibleOrderSection
        title="Internal"
        expanded={expandedSections.has("internal")}
        onToggle={() => toggleSection("internal")}
      >
        <div className="space-y-2">
          <Label htmlFor="order-note">Note</Label>
          <Textarea
            id="order-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            placeholder="Optional note visible only in admin"
          />
        </div>
      </CollapsibleOrderSection>

      <div className="flex gap-2">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="animate-spin h-4 w-4 mr-2" />
              Saving…
            </>
          ) : (
            "Save"
          )}
        </Button>
        {canCollect && (
          <Button variant="outline" onClick={handleCollect} disabled={isSaving}>
            <CreditCard className="h-4 w-4 mr-2" />
            Collect
          </Button>
        )}
      </div>
    </div>
  );
}
